import { DIR, OPPOSITE, DIRECTIONS, DIR_VECS } from '@shared/constants.js';
import { Direction } from '@shared/types.js';
import type { Grid, Player } from '@shared/types.js';

interface BotContext {
  grid: Grid;
  cols: number;
  rows: number;
  players: Player[];
}

export function botThink(bot: Player, ctx: BotContext): void {
  const enemies = ctx.players.filter(p => p.id !== bot.id && p.alive);

  let best: Direction | null = null;
  let bestScore = -Infinity;

  for (const dirName of DIRECTIONS) {
    if (OPPOSITE[dirName] === bot.dir) continue;
    const d = DIR[dirName];
    const nx = bot.x + d.x;
    const ny = bot.y + d.y;
    if (!isSafe(nx, ny, ctx)) continue;

    let score = countOpenSpace(nx, ny, 150, ctx);

    for (const e of enemies) {
      const distNow = Math.abs(bot.x - e.x) + Math.abs(bot.y - e.y);
      const distNext = Math.abs(nx - e.x) + Math.abs(ny - e.y);
      if (distNext < distNow && distNow < 5) score -= 20;
    }

    let adjacentWalls = 0;
    for (const dd of DIR_VECS) {
      if (!isSafe(nx + dd.x, ny + dd.y, ctx)) adjacentWalls++;
    }
    if (adjacentWalls === 1) score += 3;
    if (dirName === bot.dir) score += 1;
    // Small random tiebreaker so symmetric bots don't mirror each other
    score += Math.random() * 0.5;

    if (score > bestScore) { bestScore = score; best = dirName; }
  }

  if (best) bot.nextDir = best;
}

function isSafe(x: number, y: number, ctx: BotContext): boolean {
  return x >= 0 && x < ctx.cols && y >= 0 && y < ctx.rows && ctx.grid[y][x] === 0;
}

function countOpenSpace(sx: number, sy: number, max: number, ctx: BotContext): number {
  const visited = new Set<number>();
  const queue: number[] = [sx, sy];
  let head = 0;
  visited.add(sy * ctx.cols + sx);
  let count = 0;
  while (head < queue.length && count < max) {
    const x = queue[head++];
    const y = queue[head++];
    count++;
    for (const d of DIR_VECS) {
      const nx = x + d.x;
      const ny = y + d.y;
      const key = ny * ctx.cols + nx;
      if (isSafe(nx, ny, ctx) && !visited.has(key)) {
        visited.add(key);
        queue.push(nx, ny);
      }
    }
  }
  return count;
}
